import CropperClass from 'cropperjs';
import 'viewerjs/dist/viewer.css';
// import 'cropperjs/dist/cropper.css';

import { round, isEmpty } from 'lodash-es';
import { defineComponent, ref, computed, onMounted, PropType, toRefs, ComputedRef, Ref } from '@vue/composition-api';
import { i18n } from '@tt4b/ttam-i18n';
import { logger } from '@tt4b/ttam-utils';
import {
  AdgroupFormData,
  CampaignFormData,
  FullNormalFormData,
  ImageTemplate,
  ImageToUpload,
  INVENTORY_FLOW,
  MaterialSize,
} from '@tt4b/ttam-enums';
import { useMaterialVerify, useStdAccountStore, useStdObjectiveType } from '@tt4b/ttam-hooks';

import {
  CropperFillColor,
  fillCanvasByCanvas,
  generateBlackCanvas,
  generateBlurCanvas,
  getImageBasicInfo,
  showCanvas,
} from './utils';
import { trackEvent } from './../../../../utils/index';

/** 放大缩小步调 */
const ZoomStep = 0.1;

/** 通用精度 */
const CommonPrecision = 2;

/** 图片宽高类型选项 */
const ImageModeOptions = [
  {
    key: MaterialSize.Horizontal,
    size: ImageTemplate[MaterialSize.Horizontal],
    label: i18n.t('ad_creative_aspect_horizontal'),
    labelIcon: 'crop-horizontal',
    teaValue: '1',
  },
  {
    key: MaterialSize.Square,
    size: ImageTemplate[MaterialSize.Square],
    label: i18n.t('ad_creative_aspect_square'),
    labelIcon: 'crop-square',
    teaValue: '2',
  },
  {
    key: MaterialSize.Vertical720,
    size: ImageTemplate[MaterialSize.Vertical720],
    label: i18n.t('ad_creative_aspect_vertical_720p'),
    labelIcon: 'crop-vertical',
    teaValue: '4',
  },
  {
    key: MaterialSize.HorizontalInsertRectangle,
    size: ImageTemplate[MaterialSize.HorizontalInsertRectangle],
    labelIcon: 'crop-insert-rectangle',
    label: i18n.t('ad_creative_aspect_insert_rectangle'),
    teaValue: '5',
  },
  {
    key: MaterialSize.HorizontalBannerLarge,
    size: ImageTemplate[MaterialSize.HorizontalBannerLarge],
    labelIcon: 'crop-large-banner',
    label: i18n.t('ad_creative_aspect_banner_large'),
    teaValue: '6',
  },
  {
    key: MaterialSize.HorizontalBannerSmall,
    size: ImageTemplate[MaterialSize.HorizontalBannerSmall],
    labelIcon: 'crop-small-banner',
    label: i18n.t('ad_creative_aspect_banner_small'),
    teaValue: '7',
  },
];
const name = 'ttam-material-image-upload-cropper';
export default defineComponent({
  name,
  props: {
    /** 需要裁剪的图片 */
    image: { type: Object as PropType<ImageToUpload>, default: () => ({}) },
    /** 是否需要版位校验 */
    needVerify: { type: Boolean, default: true },
    /** 支持的图片类型，为空则为不限制 HORIZONTAL、SQUARE、VERTICAL_720P */
    imageModeSupport: { type: Array as PropType<string[]>, default: () => [] },
    /** 自定义裁剪的大小 */
    customCropperSize: {
      type: Object as PropType<{
        imageMode: number;
        width: number;
        height: number;
        ratio: number;
      }>,
      default: () => ({}),
    },
    /** 定义使用类型，用于埋点， create、library、cover */
    usage: { type: String, default: 'create' },
    adgroupData: {
      required: true,
      type: Object as PropType<AdgroupFormData>,
      default: () => ({}),
    },
    campaignData: {
      required: true,
      type: Object as PropType<CampaignFormData>,
      default: () => ({}),
    },
    creativeData: {
      required: true,
      type: Object as PropType<FullNormalFormData>,
      default: () => ({}),
    },
  },

  // eslint-disable-next-line max-lines-per-function
  setup(props, ctx) {
    const { adgroupData, creativeData, campaignData } = toRefs(props);
    const { accountInfo } = useStdAccountStore();
    const { rules } = useMaterialVerify({
      adgroupFormData: adgroupData,
      creativeFormData: creativeData,
    });
    const { isProductSales } = useStdObjectiveType({ campaignFormData: campaignData });

    const resultNode = ref<HTMLElement>();
    const imageNode = ref<HTMLImageElement>();

    const loading = ref(false);
    const cropper = ref<CropperClass>();
    /** 记录初始裁剪数据用于设置模糊背景 */
    const originCroppedCanvas: Ref<HTMLCanvasElement> = ref();
    /** 选中的图片 size key */
    const imageModeKeySelected = ref<MaterialSize>();
    /** 是否使用高斯模糊填充 */
    const isUseGaussian = ref(true);
    const originImg = ref<HTMLImageElement>();
    const resultCanvas = ref<HTMLCanvasElement>();
    const originRatio = computed(() =>
      round((originImg.value?.width ?? 0) / (originImg.value?.height ?? 1), CommonPrecision),
    );
    const resultSize = computed(() => ({
      width: resultCanvas.value?.width,
      height: resultCanvas.value?.height,
    }));

    /** 图片 size 选项 */
    const imageModeOptions = computed(() => {
      if (!props.imageModeSupport.length) {
        return ImageModeOptions;
      }

      return ImageModeOptions.filter(option => props.imageModeSupport.includes(option.key));
    });

    /** 是否使用自定义裁剪大小 */
    const isCustomCropperSize = computed(() => !isEmpty(props.customCropperSize));
    const customCropperSize: ComputedRef<{
      size: {
        imageMode: number;
        width: number;
        height: number;
        ratio: number;
      };
    }> = computed(() => ({ size: props.customCropperSize }));

    /** 当前图片 size，如果有自定义的则为自定义的 */
    const imageModeSelected = computed(() =>
      isCustomCropperSize.value
        ? customCropperSize.value
        : imageModeOptions.value.find(item => item.key === imageModeKeySelected.value) ?? imageModeOptions.value[0],
    );

    /** 当前图片 size 的 ratio */
    const ratioSelected = computed(() => imageModeSelected.value.size.ratio);

    /** 满足当前图片 size 的版位 */
    const legalApps = computed(() => {
      const shoppingAdsSupportPlacements = [INVENTORY_FLOW.TIKTOK, INVENTORY_FLOW.TIKTOKADNETWORK];
      const resultRules = rules.value.filter(rule => {
        const solution = rule.imageRule.resolution?.types || [];
        const selected = imageModeOptions.value.find(item => item.key === imageModeKeySelected.value)?.size;
        return Boolean(solution.find(item => item.imageMode === selected?.imageMode && item.ratio === selected.ratio));
      });
      // product sales 目前只支持 tiktok / pangle 两种版位，所以这里暂时需要做个过滤
      if (isProductSales.value) {
        return resultRules.filter(rule => shoppingAdsSupportPlacements.includes(rule.key));
      } else {
        return resultRules;
      }
    });

    /** 计算裁剪框初始位置、模糊底图位置 */
    const initialPosition = computed(() => {
      const { width = 0, height = 0 } = originImg.value ?? {};
      let startX = 0;
      let startY = 0;
      let targetWidth = width;
      let targetHeight = height;

      if (originRatio.value > ratioSelected.value) {
        targetWidth = height * ratioSelected.value;
        startX = (width - targetWidth) / 2;
      } else {
        targetHeight = width / ratioSelected.value;
        startY = (height - targetHeight) / 2;
      }

      return { startX, startY, targetWidth, targetHeight };
    });

    /** ---------- [start] 埋点相关---------- */
    const isManualOperated = false;
    /** 生成一些埋点参数 */
    function getSomeInfoForEventTrack() {
      const cropperData = cropper.value?.getData();

      return {
        aspect_available: imageModeOptions.value.map(item => item.teaValue).join(''),
        blank_area: isUseGaussian.value ? 'gaussian_blur' : CropperFillColor,
        crop_result: isManualOperated ? 'manual_edited' : 'default',
        crop_width: round(cropperData?.width ?? 0, CommonPrecision),
        crop_height: round(cropperData?.height ?? 0, CommonPrecision),
        crop_needed: originRatio.value === ratioSelected.value ? 0 : 1,
        final_width: round(resultSize.value.width ?? 0, CommonPrecision),
        final_height: round(resultSize.value.height ?? 0, CommonPrecision),
        final_ratio: ratioSelected.value,
        origin_size: props.image?.size ?? 0,
        origin_width: round(originImg.value?.width ?? 0, CommonPrecision),
        origin_height: round(originImg.value?.height ?? 0, CommonPrecision),
        origin_ratio: originRatio.value,
        upper_left_corner_x_axis: round(cropperData?.x ?? 0, CommonPrecision),
        upper_left_corner_y_axis: round(cropperData?.y ?? 0, CommonPrecision),
      };
    }
    /** ---------- [end] 埋点记录 ---------- */

    /** 加载页面图片节点 */
    function imageNodeLoaded(): Promise<boolean> {
      return new Promise(resolve => {
        if (!imageNode.value) {
          resolve(false);

          return;
        }
        imageNode.value.onload = () => resolve(true);
        imageNode.value.onerror = () => resolve(false);
        imageNode.value.src = props.image.url;
      });
    }

    /** 初始化 viewer 用于全屏预览 */
    async function initViewer(file: ImageToUpload, targetNode: HTMLElement) {
      if (!targetNode) {
        return null;
      }

      targetNode.querySelector('img') &&
        targetNode.querySelector('img').addEventListener('click', e => {
          e.preventDefault();
          e.stopPropagation();
        });
      const { default: Viewer } = await import(/* webpackChunkName: "viewerjs" */ 'viewerjs');
      const viewer = new Viewer(targetNode, {
        navbar: false,
        title: false,
        zIndex: 3000,
        hidden: () => viewer.destroy(),
        toolbar: {
          zoomIn: true,
          zoomOut: true,
          oneToOne: true,
          download: () => {
            const a = document.createElement('a');
            const suffixIndex = file.name.lastIndexOf('.');
            const suffix = file.name.substr(suffixIndex + 1);
            const accountName = (accountInfo.value.username || '').replace(/\s/g, '_');
            const fileName = `TikTokAds_${accountName}_${file.name.substr(0, suffixIndex)}.${suffix}`;
            a.href = viewer.image.src;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            trackEvent('crop_image_preview_view_download', { upload_resource: props.usage }); // 下载文件埋点
            logger.info('crop_image_preview_view_download', { upload_resource: props.usage }); // 下载文件埋点
          },
        },
      }) as import('viewerjs').default & { image: { src: string } };

      return viewer;
    }

    /** 全屏预览 viewer */
    async function fullScreenPreview() {
      const file = props.image;
      const targetNode = resultNode.value;
      const viewer = await initViewer(file, targetNode);
      viewer && viewer.show().zoomTo(1);
      trackEvent('crop_image_preview_view', { upload_resource: props.usage });
      logger.info('crop_image_preview_view', { upload_resource: props.usage });
    }

    /** 生成最终效果 */
    function generateCroppedImage() {
      const croppedCanvas = cropper.value?.getCroppedCanvas();
      if (!croppedCanvas || !originImg.value) {
        return;
      }

      const result: HTMLCanvasElement = document.createElement('canvas');
      const { width: requiredWidth, height: requiredHeight } = imageModeSelected.value.size;
      result.width = requiredWidth;
      result.height = requiredHeight;
      const baseCanvas = isUseGaussian.value
        ? generateBlurCanvas({
            img: originCroppedCanvas.value,
            requiredWidth,
            requiredHeight,
            position: initialPosition.value,
          })
        : generateBlackCanvas({ requiredWidth, requiredHeight });
      fillCanvasByCanvas(result, baseCanvas);
      fillCanvasByCanvas(result, croppedCanvas);
      resultCanvas.value = result;
      showCanvas(result, resultNode.value);
      ctx.emit('change', { imageCropped: result, image: originImg.value });
    }

    const findClosestRatio = (ratio: number, arr: typeof ImageModeOptions) => {
      // 创建一个变量来存储最接近比例的元素索引
      let closestIndex = -1;
      // 创建一个变量来存储当前最接近比例的差值
      let closestDiff = Infinity;
      // 迭代数组中的每个元素
      for (let i = 0; i < arr.length; i++) {
        const currentRatio = arr[i].size?.ratio;
        // 计算当前元素与目标比例之间的差值
        const diff = Math.abs(currentRatio - ratio);
        // 检查是否应该更新 closestIndex 和 closestDiff
        // 根据输入比例和当前元素的大小关系来判断
        if (diff < closestDiff && (ratio === 1 || (ratio < 1 && currentRatio < 1) || (ratio > 1 && currentRatio > 1))) {
          closestIndex = i;
          closestDiff = diff;
        }
      }
      // 返回最接近比例的元素
      return arr[closestIndex];
    };
    const initCropperRatio = () => {
      const closestRatioItem = findClosestRatio(originRatio.value, imageModeOptions.value) || imageModeOptions.value[0];
      imageModeKeySelected.value = closestRatioItem.key;
    };
    /** 初始化图片裁剪器，流程的起点 */
    async function initCropper() {
      loading.value = true;
      originImg.value = await getImageBasicInfo(props.image.url);
      await imageNodeLoaded().then(initCropperRatio);

      const config = {
        aspectRatio: ratioSelected.value,
        zoomOnWheel: false,
        minContainerWidth: 720,
        minContainerHeight: 460,
        data: {
          x: initialPosition.value.startX,
          y: initialPosition.value.startY,
          width: initialPosition.value.targetWidth,
          height: initialPosition.value.targetHeight,
        },
        ready: () => {
          const containerData = cropper.value?.getContainerData();
          const cropBoxData = cropper.value?.getCropBoxData();
          const newCropBoxWidth = (cropBoxData?.height ?? 0) * ratioSelected.value;
          cropper.value &&
            cropper.value.setCropBoxData({
              width: newCropBoxWidth,
              left: ((containerData?.width ?? 0) - newCropBoxWidth) / 2,
            });
          originCroppedCanvas.value = cropper.value?.getCroppedCanvas();
          loading.value = false;
          // 延迟一会儿生成，解决线上偶尔截图失败的问题
          setTimeout(() => {
            generateCroppedImage();
          }, 0);
        },
        cropend: () => generateCroppedImage(),
        // cropmove: () => (teaVars.value.crop_result = 'manual_edited'),
        // zoom: () => (teaVars.value.crop_result = 'manual_edited'),
      };

      if (imageNode.value) {
        const { default: Cropper } = await import(/* webpackChunkName: "cropperjs" */ 'cropperjs');
        cropper.value = new Cropper(imageNode.value, config);
        originCroppedCanvas.value = cropper.value?.getCroppedCanvas();
      }
    }

    /** 重置裁剪框 */
    function resetCropper() {
      if (cropper.value) {
        cropper.value.reset();
        cropper.value.setData({
          x: initialPosition.value.startX,
          y: initialPosition.value.startY,
          width: initialPosition.value.targetWidth,
          height: initialPosition.value.targetHeight,
        });
      }
      generateCroppedImage();
    }

    /** 是否使用高斯模糊改变事件 */
    function onUseGaussianChange() {
      generateCroppedImage();
    }

    /** 图片宽高类型切换事件 */
    function onImageModeChange() {
      cropper.value && cropper.value.setAspectRatio(ratioSelected.value);
      resetCropper();
      trackEvent('crop_image_change_aspect', { upload_resource: props.usage });
      logger.info('crop_image_change_aspect', { upload_resource: props.usage });
    }

    /** 裁剪范围缩小 */
    function zoomInCropper() {
      cropper.value && cropper.value.zoom(ZoomStep);
      generateCroppedImage();
      trackEvent('crop_image_zoom_in', { upload_resource: props.usage });
      logger.info('crop_image_zoom_in', { upload_resource: props.usage });
    }

    /** 裁剪范围扩大 */
    function zoomOutCropper() {
      cropper.value && cropper.value.zoom(-ZoomStep);
      generateCroppedImage();
      trackEvent('crop_image_zoom_out', { upload_resource: props.usage });
      logger.info('crop_image_zoom_out', { upload_resource: props.usage });
    }

    onMounted(() => {
      initCropper();
    });

    return {
      name,
      imageNode,
      resultNode,

      loading,
      resultSize,
      imageModeOptions,
      imageModeKeySelected,
      imageModeSelected,
      legalApps,
      isUseGaussian,
      isCustomCropperSize,

      fullScreenPreview,
      resetCropper,
      zoomInCropper,
      zoomOutCropper,
      onImageModeChange,
      onUseGaussianChange,
      getSomeInfoForEventTrack,
    };
  },
});
